/**
 * ============================================================================
 * TMA CUSTOM ICON REGISTRY
 * ============================================================================
 * All icons are 24×24 SVG, stroke-based, 1.5–1.6px stroke-width.
 * Designed for dual-state use: outline (default nav) → solid (active nav).
 *
 * USAGE IN FUSE NAV ITEMS:
 *   icon: 'tma:patient'
 *   icon: 'tma:physician'
 *   icon: 'tma:tminsights'
 *   icon: 'tma:tmai'
 *
 * REGISTRATION:
 *   Import TmaIconsModule in AppModule or CoreModule.
 *   The FuseSvgIconsService registers all icons under the 'tma' namespace.
 *
 * ANGULAR TEMPLATE:
 *   <mat-icon svgIcon="tma:patient"></mat-icon>
 *   <mat-icon svgIcon="tma:physician" class="icon-size-5"></mat-icon>
 *
 * ============================================================================
 * ICON CATALOGUE — 73 icons total
 * ============================================================================
 *
 * ── AI & Brand (2) ──────────────────────────────────────────────────────────
 *   tminsights        TMInsights AI — neural constellation mark
 *   tmai              TMAI assistant — crystalline spark mark
 *
 * ── People & Roles (6) ──────────────────────────────────────────────────────
 *   patient           Patient (clinical subject) — person + pulse line
 *   physician         Physician (provider) — person + medical cross badge
 *   physicians        Physicians (plural/group) — two persons + cross badge
 *   referring-physician   Referring physician — person + outbound arrow
 *   supervising-physician Supervising physician — person + upward chevron
 *   users             System users — two persons, no badge
 *
 * ── Analytics Navigation (19) ────────────────────────────────────────────────
 *   practice-dashboard, triad-dashboard, revenue-enhancement,
 *   chargemaster-review, reimbursement-variance, sequencing-recovery,
 *   operations-metrics, clinic-performance, work-rvu, charge-processing,
 *   new-patient-analytics, quality-compliance, medicare-compliance,
 *   quality-incentives, patient-outcomes, data-extraction, onboarding,
 *   analytics-dashboard, triad-overview
 *
 * ── Admin Navigation (12) ────────────────────────────────────────────────────
 *   admin-dashboard, organizations, user-management, silver-extraction,
 *   silver-extraction-v2, report-wizard, gandalf, try-query,
 *   location-metrics, referring-doctors, patient-retention, diagnosis-analysis
 *
 * ── Analytics Pipeline (14) ──────────────────────────────────────────────────
 *   select-context, data-ingestion, copper-ingestion, bronze-ingestion,
 *   facility-classification, payer-classification, denial-classification,
 *   diagnosis-classification, physician-classification,
 *   adjustment-classification, fee-schedule-build, medicare-rates,
 *   silver-analytics, gold-aggregation
 *
 * ── Ingestion Tools (11) ─────────────────────────────────────────────────────
 *   map-to-tma, encounter-analytics, encounter-flow, session-cpt-analysis,
 *   import-validation, data-cleanup, audit-logs, orchestration-monitor,
 *   ingestion-test, medicare-ingestion, cms-data-loader
 *
 * ── Data Model Entities (18) ─────────────────────────────────────────────────
 *   dm-patient, dm-encounter, dm-service-line, dm-cpt-code, dm-icd10,
 *   dm-rendering-physician, dm-payer, dm-facility, dm-payment, dm-adjustment,
 *   dm-denial, dm-fee-schedule, dm-referring-physician,
 *   dm-supervising-physician, dm-chargemaster, dm-modifier, dm-place-of-service
 *   (+ dm-physicians for group context)
 *
 * ============================================================================
 */

export interface TmaIconDef {
  /** Icon name used in svgIcon="tma:{name}" */
  name: string;
  /** Raw SVG inner content (no <svg> wrapper — injected by FuseSvgIconsService) */
  outline: string;
  /** Solid/filled variant for active nav state */
  solid: string;
}

// ─── AI & BRAND ──────────────────────────────────────────────────────────────

/**
 * TMInsights — neural constellation mark
 * Three nodes connected by weighted edges, with a central spark.
 * Represents AI-powered insight emerging from connected data points.
 */
const TMINSIGHTS: TmaIconDef = {
  name: 'tminsights',
  outline: `
    <circle cx="12" cy="12" r="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="5"  cy="6"  r="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="19" cy="6"  r="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="5"  cy="19" r="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="19" cy="19" r="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="6.1"  y1="7.1"  x2="10.6" y2="10.6" stroke="currentColor" stroke-width="1"   stroke-linecap="round" opacity="0.7"/>
    <line x1="17.9" y1="7.1"  x2="13.4" y2="10.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" opacity="0.9"/>
    <line x1="6.1"  y1="17.9" x2="10.6" y2="13.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" opacity="0.9"/>
    <line x1="17.9" y1="17.9" x2="13.4" y2="13.4" stroke="currentColor" stroke-width="1"   stroke-linecap="round" opacity="0.7"/>
    <line x1="5"    y1="7.5"  x2="5"    y2="17.5"  stroke="currentColor" stroke-width="0.8" stroke-linecap="round" opacity="0.4"/>
    <line x1="19"   y1="7.5"  x2="19"   y2="17.5"  stroke="currentColor" stroke-width="0.8" stroke-linecap="round" opacity="0.4"/>
    <path d="M12 8.5 L13 11 L15.5 12 L13 13 L12 15.5 L11 13 L8.5 12 L11 11 Z"
          fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="5"  cy="6"  r="2" fill="currentColor"/>
    <circle cx="19" cy="6"  r="2" fill="currentColor"/>
    <circle cx="5"  cy="19" r="2" fill="currentColor"/>
    <circle cx="19" cy="19" r="2" fill="currentColor"/>
    <line x1="6.4"  y1="7.4"  x2="10.6" y2="10.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" opacity="0.6"/>
    <line x1="17.6" y1="7.4"  x2="13.4" y2="10.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="6.4"  y1="16.6" x2="10.6" y2="13.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="17.6" y1="16.6" x2="13.4" y2="13.4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" opacity="0.6"/>
    <path d="M12 8 L13.2 10.8 L16 12 L13.2 13.2 L12 16 L10.8 13.2 L8 12 L10.8 10.8 Z"
          fill="currentColor" stroke="none"/>`
};

/**
 * TMAI — crystalline spark mark
 * A multi-pointed spark with radiating lines and a precise geometric center.
 * Distinct from TMInsights: where TMInsights is about connected data,
 * TMAI is about generative intelligence — the spark that creates.
 */
const TMAI: TmaIconDef = {
  name: 'tmai',
  outline: `
    <path d="M12 3 L13.5 8.5 L19 10 L13.5 11.5 L12 17 L10.5 11.5 L5 10 L10.5 8.5 Z"
          fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M19 17 L19.8 19.2 L22 20 L19.8 20.8 L19 23 L18.2 20.8 L16 20 L18.2 19.2 Z"
          fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M5 3 L5.6 4.8 L7.5 5.5 L5.6 6.2 L5 8 L4.4 6.2 L2.5 5.5 L4.4 4.8 Z"
          fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/>
    <line x1="12" y1="1"  x2="12" y2="2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="12" y1="21.8" x2="12" y2="23" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="1"  y1="10" x2="4.2" y2="10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="19.8" y1="10" x2="23" y2="10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <path d="M12 2.5 L13.8 8.2 L19.5 10 L13.8 11.8 L12 17.5 L10.2 11.8 L4.5 10 L10.2 8.2 Z"
          fill="currentColor" stroke="none"/>
    <path d="M19 16.5 L19.9 19.1 L22.5 20 L19.9 20.9 L19 23.5 L18.1 20.9 L15.5 20 L18.1 19.1 Z"
          fill="currentColor" stroke="none"/>
    <path d="M5 2.5 L5.7 4.8 L8 5.5 L5.7 6.2 L5 8.5 L4.3 6.2 L2 5.5 L4.3 4.8 Z"
          fill="currentColor" stroke="none"/>
    <line x1="12" y1="1"   x2="12" y2="2"   stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12" y1="22"  x2="12" y2="23"  stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="1"  y1="10"  x2="4"   y2="10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="20" y1="10"  x2="23"  y2="10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`
};

// ─── PEOPLE & ROLES ──────────────────────────────────────────────────────────

const PATIENT: TmaIconDef = {
  name: 'patient',
  outline: `
    <circle cx="12" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M8 20.5 Q10 18.5 12 20 Q14 21.5 16 20.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="7" r="4" fill="currentColor"/>
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <path d="M8 20.5 Q10 18.5 12 20 Q14 21.5 16 20.5" fill="none" stroke="white" stroke-width="1.2" stroke-linecap="round"/>`
};

const PHYSICIAN: TmaIconDef = {
  name: 'physician',
  outline: `
    <circle cx="11" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M3 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="19.5" cy="17.5" r="3" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="19.5" y1="15.8" x2="19.5" y2="19.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="17.8" y1="17.5" x2="21.2" y2="17.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <circle cx="11" cy="7" r="4" fill="currentColor"/>
    <path d="M3 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <circle cx="19.5" cy="17.5" r="3.2" fill="currentColor"/>
    <line x1="19.5" y1="15.5" x2="19.5" y2="19.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="17.5" y1="17.5" x2="21.5" y2="17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const PHYSICIANS: TmaIconDef = {
  name: 'physicians',
  outline: `
    <circle cx="8"  cy="7" r="3"   fill="none" stroke="currentColor" stroke-width="1.4"/>
    <circle cx="16" cy="7" r="3"   fill="none" stroke="currentColor" stroke-width="1.4"/>
    <path d="M2 20c0-3.9 3.1-7 6-7s4.5 1.5 6 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M14 17c1.5-2.5 3.5-4 6-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <circle cx="20" cy="17" r="2.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="20" y1="15.5" x2="20" y2="18.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="18.5" y1="17" x2="21.5" y2="17" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <circle cx="8"  cy="7" r="3.5" fill="currentColor"/>
    <circle cx="16" cy="7" r="3.5" fill="currentColor"/>
    <path d="M2 20c0-3.9 3.1-7 6-7s4.5 1.5 6 4" fill="currentColor" stroke="none"/>
    <path d="M14 17c1.5-2.5 3.5-4 6-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="20" cy="17" r="2.8" fill="currentColor"/>
    <line x1="20" y1="15.2" x2="20" y2="18.8" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="18.2" y1="17" x2="21.8" y2="17" stroke="white" stroke-width="1.4" stroke-linecap="round"/>`
};

const REFERRING_PHYSICIAN: TmaIconDef = {
  name: 'referring-physician',
  outline: `
    <circle cx="8" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M2 21c0-4.2 3.1-7 6-7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="14" y1="11" x2="21" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M18 8.5 L21 11 L18 13.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="8" cy="7" r="4" fill="currentColor"/>
    <path d="M2 21c0-4.2 3.1-7 6-7" fill="currentColor" stroke="none"/>
    <path d="M2 21c0-4.2 3.1-7 6-7" fill="none" stroke="currentColor" stroke-width="0.5"/>
    <line x1="14" y1="11" x2="21" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <path d="M18 8.5 L21 11 L18 13.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
};

const SUPERVISING_PHYSICIAN: TmaIconDef = {
  name: 'supervising-physician',
  outline: `
    <circle cx="12" cy="9" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M8.5 4.5 L12 2 L15.5 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="12" y1="2" x2="12" y2="5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="9" r="4" fill="currentColor"/>
    <path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <path d="M8.5 4.5 L12 2 L15.5 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="12" y1="2" x2="12" y2="5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`
};

const USERS: TmaIconDef = {
  name: 'users',
  outline: `
    <circle cx="8"  cy="7" r="3"   fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="17" cy="7" r="3"   fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M2 21c0-3.9 3.1-7 6-7s6 3.1 6 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M18 14c2.8 0 5 2.2 5 5v2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <circle cx="8"  cy="7" r="3.5" fill="currentColor"/>
    <circle cx="17" cy="7" r="3.5" fill="currentColor"/>
    <path d="M2 21c0-3.9 3.1-7 6-7s6 3.1 6 7" fill="currentColor"/>
    <path d="M18 14c2.8 0 5 2.2 5 5v2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
};

// ─── ANALYTICS NAVIGATION ────────────────────────────────────────────────────

const PRACTICE_DASHBOARD: TmaIconDef = {
  name: 'practice-dashboard',
  outline: `
    <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <rect x="6" y="9"  width="3" height="7" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="10.5" y="11" width="3" height="5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="15" y="7" width="3" height="9"  fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor"/>
    <rect x="6"   y="9"  width="3" height="7" fill="white"/>
    <rect x="10.5" y="11" width="3" height="5" fill="white"/>
    <rect x="15"  y="7"  width="3" height="9" fill="white"/>`
};

const TRIAD_DASHBOARD: TmaIconDef = {
  name: 'triad-dashboard',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M12 7v5l3.5 2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <path d="M12 7v5l3.5 2" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
};

/**
 * TMA Dashboard — header tile + 3 ascending KPI tiles.
 * Reads as "your TMA HQ: a dashboard with the key practice metrics".
 */
const TMA_DASHBOARD: TmaIconDef = {
  name: 'tma-dashboard',
  outline: `
    <rect x="3" y="3" width="18" height="18" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="6" cy="6" r="0.9" fill="currentColor"/>
    <line x1="8.5" y1="6" x2="14.5" y2="6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <rect x="5.5"  y="14" width="2.8" height="4"   rx="0.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="10.6" y="12" width="2.8" height="6"   rx="0.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="15.7" y="10" width="2.8" height="8"   rx="0.5" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="18" rx="2.5" fill="currentColor"/>
    <line x1="3" y1="9" x2="21" y2="9" stroke="white" stroke-width="1.3" opacity="0.75"/>
    <circle cx="6" cy="6" r="0.9" fill="white"/>
    <line x1="8.5" y1="6" x2="14.5" y2="6" stroke="white" stroke-width="1.2" stroke-linecap="round" opacity="0.8"/>
    <rect x="5.5"  y="14" width="2.8" height="4"   rx="0.5" fill="white"/>
    <rect x="10.6" y="12" width="2.8" height="6"   rx="0.5" fill="white"/>
    <rect x="15.7" y="10" width="2.8" height="8"   rx="0.5" fill="white"/>`
};

/**
 * User Dashboard — person silhouette inside a dashboard frame, with stat tiles.
 * Reads as "your personal view of the metrics that matter to you".
 */
const USER_DASHBOARD: TmaIconDef = {
  name: 'user-dashboard',
  outline: `
    <rect x="3" y="3" width="18" height="18" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <circle cx="12" cy="8.5" r="2.2" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <path d="M7.6 14.5c0.9-2 2.6-3.1 4.4-3.1s3.5 1.1 4.4 3.1" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <rect x="6"  y="16.6" width="3" height="2" rx="0.4" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="10.5" y="16.6" width="3" height="2" rx="0.4" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="15" y="16.6" width="3" height="2" rx="0.4" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="18" rx="2.5" fill="currentColor"/>
    <circle cx="12" cy="8.5" r="2.2" fill="white"/>
    <path d="M7.6 14.5c0.9-2 2.6-3.1 4.4-3.1s3.5 1.1 4.4 3.1" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <rect x="6"    y="16.6" width="3" height="2" rx="0.4" fill="white" opacity="0.85"/>
    <rect x="10.5" y="16.6" width="3" height="2" rx="0.4" fill="white" opacity="0.85"/>
    <rect x="15"   y="16.6" width="3" height="2" rx="0.4" fill="white" opacity="0.85"/>`
};

/**
 * Practice Overview — central hub with 4 satellite tiles.
 * Reads as "everything about the practice at a glance".
 */
const PRACTICE_OVERVIEW: TmaIconDef = {
  name: 'practice-overview',
  outline: `
    <circle cx="12" cy="12" r="2.6" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <rect x="9"  y="2.5" width="6" height="4" rx="0.8" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <rect x="9"  y="17.5" width="6" height="4" rx="0.8" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <rect x="2.5" y="10" width="4" height="4" rx="0.8" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <rect x="17.5" y="10" width="4" height="4" rx="0.8" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <line x1="12" y1="6.5"  x2="12" y2="9.4"  stroke="currentColor" stroke-width="1.2" opacity="0.7"/>
    <line x1="12" y1="14.6" x2="12" y2="17.5" stroke="currentColor" stroke-width="1.2" opacity="0.7"/>
    <line x1="6.5" y1="12" x2="9.4" y2="12" stroke="currentColor" stroke-width="1.2" opacity="0.7"/>
    <line x1="14.6" y1="12" x2="17.5" y2="12" stroke="currentColor" stroke-width="1.2" opacity="0.7"/>`,
  solid: `
    <line x1="12" y1="6.5"  x2="12" y2="9.4"  stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="12" y1="14.6" x2="12" y2="17.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="6.5" y1="12" x2="9.4" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="14.6" y1="12" x2="17.5" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="12" cy="12" r="2.6" fill="currentColor"/>
    <rect x="9"   y="2.5"  width="6" height="4" rx="0.8" fill="currentColor"/>
    <rect x="9"   y="17.5" width="6" height="4" rx="0.8" fill="currentColor"/>
    <rect x="2.5" y="10"   width="4" height="4" rx="0.8" fill="currentColor"/>
    <rect x="17.5" y="10"  width="4" height="4" rx="0.8" fill="currentColor"/>`
};

/**
 * Practice Detail — three stacked cards going from back to foreground.
 * Reads as "drill into the layers / one dashboard among many".
 */
const PRACTICE_DETAIL: TmaIconDef = {
  name: 'practice-detail',
  outline: `
    <rect x="8.5" y="2"  width="13" height="13" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.3" opacity="0.4"/>
    <rect x="5.5" y="5"  width="13" height="13" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.3" opacity="0.7"/>
    <rect x="2.5" y="8"  width="13" height="13" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="5"  y1="12"   x2="13" y2="12"   stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="5"  y1="15"   x2="11" y2="15"   stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="5"  y1="18"   x2="12.5" y2="18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <rect x="8.5" y="2"  width="13" height="13" rx="1.6" fill="currentColor" opacity="0.35"/>
    <rect x="5.5" y="5"  width="13" height="13" rx="1.6" fill="currentColor" opacity="0.6"/>
    <rect x="2.5" y="8"  width="13" height="13" rx="1.6" fill="currentColor"/>
    <line x1="5"  y1="12"   x2="13" y2="12"   stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="5"  y1="15"   x2="11" y2="15"   stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="5"  y1="18"   x2="12.5" y2="18" stroke="white" stroke-width="1.4" stroke-linecap="round"/>`
};

const REVENUE_ENHANCEMENT: TmaIconDef = {
  name: 'revenue-enhancement',
  outline: `
    <rect x="2" y="7" width="20" height="13" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="6"  y1="11" x2="6"  y2="16" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="9"  y1="13" x2="9"  y2="16" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="12" y1="9"  x2="12" y2="16" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M15 14 L17 11 L19 12.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <rect x="2" y="7" width="20" height="13" rx="2" fill="currentColor"/>
    <line x1="6"  y1="11" x2="6"  y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="9"  y1="13" x2="9"  y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="12" y1="9"  x2="12" y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M15 14 L17 11 L19 12.5" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`
};

const CHARGEMASTER_REVIEW: TmaIconDef = {
  name: 'chargemaster-review',
  outline: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <line x1="8" y1="13" x2="11" y2="13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="13" y1="13" x2="16" y2="13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="8" y1="16" x2="11" y2="16" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="13" y1="16" x2="16" y2="16" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="currentColor"/>
    <polyline points="14,2 14,8 20,8" fill="white" stroke="white" stroke-width="1.2"/>
    <line x1="8"  y1="13" x2="11" y2="13" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="13" y1="13" x2="16" y2="13" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="8"  y1="16" x2="11" y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="13" y1="16" x2="16" y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const REIMBURSEMENT_VARIANCE: TmaIconDef = {
  name: 'reimbursement-variance',
  outline: `
    <path d="M3 20 L8 12 L12 17 L15 13 L20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="3" y1="8" x2="21" y2="8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-dasharray="3 2"/>`,
  solid: `
    <path d="M3 20 L8 12 L12 17 L15 13 L20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="3" y1="8" x2="21" y2="8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="3 2"/>`
};

const SEQUENCING_RECOVERY: TmaIconDef = {
  name: 'sequencing-recovery',
  outline: `
    <polyline points="5,17 9,13 13,15 19,7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    <polyline points="15,7 19,7 19,11" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <polyline points="5,17 9,13 13,15 19,7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    <polyline points="15,7 19,7 19,11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
};

const OPERATIONS_METRICS: TmaIconDef = {
  name: 'operations-metrics',
  outline: `
    <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="12" y1="20" x2="12" y2="4"  stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="6"  y1="20" x2="6"  y2="14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`,
  solid: `
    <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <line x1="12" y1="20" x2="12" y2="4"  stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <line x1="6"  y1="20" x2="6"  y2="14" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>`
};

const CLINIC_PERFORMANCE: TmaIconDef = {
  name: 'clinic-performance',
  outline: `
    <path d="M3 21h18M5 21V9l7-6 7 6v12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    <rect x="9"  y="14" width="3" height="4" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="13" y="10" width="3" height="3" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <rect x="8"  y="10" width="3" height="3" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <path d="M3 21h18M5 21V9l7-6 7 6v12" fill="currentColor" stroke="currentColor" stroke-width="0.8" stroke-linejoin="round"/>
    <rect x="9"  y="14" width="3" height="4" fill="white"/>
    <rect x="13" y="10" width="3" height="3" fill="white"/>
    <rect x="8"  y="10" width="3" height="3" fill="white"/>`
};

const WORK_RVU: TmaIconDef = {
  name: 'work-rvu',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M8 15 C8 15 9.5 9 12 9 C14.5 9 16 15 16 15" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <path d="M8 15 C8 15 9.5 9 12 9 C14.5 9 16 15 16 15" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="9" y1="13" x2="15" y2="13" stroke="white" stroke-width="1.4" stroke-linecap="round"/>`
};

const CHARGE_PROCESSING: TmaIconDef = {
  name: 'charge-processing',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="12,7 12,12 15,14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <polyline points="12,7 12,12 15,14" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
};

const NEW_PATIENT_ANALYTICS: TmaIconDef = {
  name: 'new-patient-analytics',
  outline: `
    <circle cx="9" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M2 21c0-3.9 3.1-7 7-7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="17" y1="11" x2="17" y2="17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="14" y1="14" x2="20" y2="14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`,
  solid: `
    <circle cx="9" cy="7" r="4" fill="currentColor"/>
    <path d="M2 21c0-3.9 3.1-7 7-7" fill="currentColor"/>
    <line x1="17" y1="11" x2="17" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <line x1="14" y1="14" x2="20" y2="14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
};

const QUALITY_COMPLIANCE: TmaIconDef = {
  name: 'quality-compliance',
  outline: `
    <path d="M12 2L4 6v6c0 5.5 3.4 10.7 8 12 4.6-1.3 8-6.5 8-12V6l-8-4z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="9,12 11,14 15,10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <path d="M12 2L4 6v6c0 5.5 3.4 10.7 8 12 4.6-1.3 8-6.5 8-12V6l-8-4z" fill="currentColor"/>
    <polyline points="9,12 11,14 15,10" fill="none" stroke="white" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`
};

const MEDICARE_COMPLIANCE: TmaIconDef = {
  name: 'medicare-compliance',
  outline: `
    <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="12" cy="14.5" r="3" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="12" y1="12.5" x2="12" y2="14.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="12" y1="14.5" x2="13.5" y2="14.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <rect x="3" y="4" width="18" height="16" rx="2" fill="currentColor"/>
    <line x1="3" y1="9" x2="21" y2="9" stroke="white" stroke-width="1.3"/>
    <circle cx="12" cy="14.5" r="3" fill="white"/>
    <line x1="12" y1="12.5" x2="12" y2="14.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12" y1="14.5" x2="13.5" y2="14.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`
};

const QUALITY_INCENTIVES: TmaIconDef = {
  name: 'quality-incentives',
  outline: `
    <path d="M12 3l2.5 5 5.5.8-4 3.9.9 5.5L12 15.5l-4.9 2.7.9-5.5L4 8.8l5.5-.8z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M8 21h8M12 17v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <path d="M12 3l2.5 5 5.5.8-4 3.9.9 5.5L12 15.5l-4.9 2.7.9-5.5L4 8.8l5.5-.8z" fill="currentColor"/>
    <path d="M8 21h8M12 17v4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`
};

const PATIENT_OUTCOMES: TmaIconDef = {
  name: 'patient-outcomes',
  outline: `
    <path d="M12 21C12 21 4 16 4 9a4 4 0 018 0 4 4 0 018 0c0 7-8 12-8 12z" fill="none" stroke="currentColor" stroke-width="1.6"/>`,
  solid: `
    <path d="M12 21C12 21 4 16 4 9a4 4 0 018 0 4 4 0 018 0c0 7-8 12-8 12z" fill="currentColor"/>`
};

const DATA_EXTRACTION: TmaIconDef = {
  name: 'data-extraction',
  outline: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="3" y1="8"  x2="21" y2="8"  stroke="currentColor" stroke-width="1.3"/>
    <line x1="3" y1="13" x2="21" y2="13" stroke="currentColor" stroke-width="1.3"/>
    <line x1="9" y1="8"  x2="9"  y2="21" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor"/>
    <line x1="3" y1="8"  x2="21" y2="8"  stroke="white" stroke-width="1.3"/>
    <line x1="3" y1="13" x2="21" y2="13" stroke="white" stroke-width="1.3"/>
    <line x1="9" y1="8"  x2="9"  y2="21" stroke="white" stroke-width="1.3"/>`
};

const ONBOARDING: TmaIconDef = {
  name: 'onboarding',
  outline: `
    <path d="M4.5 16.5l-1-5 5.5-1 1-5.5 5.5 2.5 3.5-4 2 6-5.5 1.5-1 5.5-10-1z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>`,
  solid: `
    <path d="M4.5 16.5l-1-5 5.5-1 1-5.5 5.5 2.5 3.5-4 2 6-5.5 1.5-1 5.5-10-1z" fill="currentColor"/>`
};

const ANALYTICS_DASHBOARD: TmaIconDef = {
  name: 'analytics-dashboard',
  outline: `
    <rect x="3"  y="3"  width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <rect x="14" y="3"  width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <rect x="3"  y="14" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/>`,
  solid: `
    <rect x="3"  y="3"  width="7" height="7" rx="1.5" fill="currentColor"/>
    <rect x="14" y="3"  width="7" height="7" rx="1.5" fill="currentColor"/>
    <rect x="3"  y="14" width="7" height="7" rx="1.5" fill="currentColor"/>
    <rect x="14" y="14" width="7" height="7" rx="1.5" fill="currentColor"/>`
};

const TRIAD_OVERVIEW: TmaIconDef = {
  name: 'triad-overview',
  outline: `
    <rect x="2" y="6" width="20" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M10 9l5 3-5 3V9z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="8" y1="4" x2="16" y2="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <rect x="2" y="6" width="20" height="14" rx="2" fill="currentColor"/>
    <path d="M10 9l5 3-5 3V9z" fill="white"/>
    <line x1="8" y1="4" x2="16" y2="4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`
};

// ─── ADMIN NAVIGATION ────────────────────────────────────────────────────────

const ADMIN_DASHBOARD: TmaIconDef = {
  name: 'admin-dashboard',
  outline: `
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="currentColor" stroke-width="1.4"/>`,
  solid: `
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="currentColor"/>
    <polyline points="9,22 9,12 15,12 15,22" fill="white" stroke="white" stroke-width="1.2"/>`
};

const ORGANIZATIONS: TmaIconDef = {
  name: 'organizations',
  outline: `
    <path d="M3 21h18M5 21V9l7-6 7 6v12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    <rect x="9"   y="13" width="2.5" height="3" fill="none" stroke="currentColor" stroke-width="1.2"/>
    <rect x="12.5" y="13" width="2.5" height="3" fill="none" stroke="currentColor" stroke-width="1.2"/>
    <rect x="10"  y="9"  width="3"   height="2.5" fill="none" stroke="currentColor" stroke-width="1.2"/>`,
  solid: `
    <path d="M3 21h18M5 21V9l7-6 7 6v12" fill="currentColor" stroke="currentColor" stroke-width="0.8"/>
    <rect x="9"    y="13" width="2.5" height="3" fill="white"/>
    <rect x="12.5" y="13" width="2.5" height="3" fill="white"/>
    <rect x="10"   y="9"  width="3"   height="2.5" fill="white"/>`
};

const USER_MANAGEMENT: TmaIconDef = {
  name: 'user-management',
  outline: `
    <circle cx="9"  cy="7" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="17" cy="7" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M2 21c0-3.3 3.1-6 7-6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M22 21c0-3.3-3.1-6-7-6s-7 2.7-7 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <circle cx="9"  cy="7" r="3.5" fill="currentColor"/>
    <circle cx="17" cy="7" r="3.5" fill="currentColor"/>
    <path d="M2 21c0-3.3 3.1-6 7-6s7 2.7 7 6" fill="currentColor"/>
    <path d="M17 15c3 0 5 2.2 5 5v1" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`
};

const SILVER_EXTRACTION: TmaIconDef = {
  name: 'silver-extraction',
  outline: `
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="7,10 12,15 17,10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" fill="currentColor" stroke="currentColor" stroke-width="1.3"/>
    <polyline points="7,10 12,15 17,10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
};

const SILVER_EXTRACTION_V2: TmaIconDef = {
  name: 'silver-extraction-v2',
  outline: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="3" y1="8" x2="21" y2="8" stroke="currentColor" stroke-width="1.2"/>
    <line x1="9" y1="8" x2="9" y2="21" stroke="currentColor" stroke-width="1.2"/>
    <polyline points="13,13 15,15 19,11" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor"/>
    <line x1="3" y1="8" x2="21" y2="8" stroke="white" stroke-width="1.3"/>
    <line x1="9" y1="8" x2="9" y2="21" stroke="white" stroke-width="1.3"/>
    <polyline points="13,13 15,15 19,11" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
};

const REPORT_WIZARD: TmaIconDef = {
  name: 'report-wizard',
  outline: `
    <path d="M12 2l1.5 4.5h4.5l-3.5 2.5 1.5 4.5L12 11l-4 2.5 1.5-4.5L6 6.5h4.5z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="12" y1="13" x2="12" y2="21" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-dasharray="2 1.5"/>`,
  solid: `
    <path d="M12 2l1.5 4.5h4.5l-3.5 2.5 1.5 4.5L12 11l-4 2.5 1.5-4.5L6 6.5h4.5z" fill="currentColor"/>
    <line x1="12" y1="13" x2="12" y2="21" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-dasharray="2 1.5"/>`
};

const GANDALF: TmaIconDef = {
  name: 'gandalf',
  outline: `
    <path d="M14.7 6.3a1 1 0 010 1.4l-8 8a1 1 0 01-.7.3H4v-2l8-8a1 1 0 011.4 0l1.3 1.3z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M7 7l-2 2-2-2 2-2 2 2z" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <path d="M19 5l-2 2-2-2 2-2 2 2z" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <path d="M14.7 6.3a1 1 0 010 1.4l-8 8a1 1 0 01-.7.3H4v-2l8-8a1 1 0 011.4 0l1.3 1.3z" fill="currentColor"/>
    <path d="M7 7l-2 2-2-2 2-2 2 2z" fill="currentColor"/>
    <path d="M19 5l-2 2-2-2 2-2 2 2z" fill="currentColor"/>`
};

const TRY_QUERY: TmaIconDef = {
  name: 'try-query',
  outline: `
    <rect x="3" y="3" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M7 8l2 2-2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <line x1="11" y1="12" x2="16" y2="12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="16" rx="2" fill="currentColor"/>
    <path d="M7 8l2 2-2 2" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <line x1="11" y1="12" x2="16" y2="12" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const LOCATION_METRICS: TmaIconDef = {
  name: 'location-metrics',
  outline: `
    <path d="M12 2C8.7 2 6 4.7 6 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <circle cx="12" cy="8" r="2.2" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="2" y1="20" x2="22" y2="20" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-dasharray="3 2"/>`,
  solid: `
    <path d="M12 2C8.7 2 6 4.7 6 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6z" fill="currentColor"/>
    <circle cx="12" cy="8" r="2.2" fill="white"/>
    <line x1="2" y1="20" x2="22" y2="20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="3 2"/>`
};

const REFERRING_DOCTORS: TmaIconDef = {
  name: 'referring-doctors',
  outline: `
    <circle cx="8"  cy="8" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="18" cy="8" r="2.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M2 21c0-3.3 2.7-6 6-6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M11 16l4-3 4 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="8"  cy="8" r="3.5" fill="currentColor"/>
    <circle cx="18" cy="8" r="3"   fill="currentColor"/>
    <path d="M2 21c0-3.3 2.7-6 6-6s6 2.7 6 6" fill="currentColor"/>
    <path d="M11 16l4-3 4 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
};

const PATIENT_RETENTION: TmaIconDef = {
  name: 'patient-retention',
  outline: `
    <circle cx="9" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M3 21c0-4.4 3.6-8 8-8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <polyline points="16,11 18,13 22,9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="9" cy="7" r="4" fill="currentColor"/>
    <path d="M3 21c0-4.4 3.6-8 8-8" fill="currentColor" stroke="none"/>
    <polyline points="16,11 18,13 22,9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
};

const DIAGNOSIS_ANALYSIS: TmaIconDef = {
  name: 'diagnosis-analysis',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="12" y1="8"  x2="12" y2="13" stroke="currentColor" stroke-width="2"   stroke-linecap="round"/>
    <circle cx="12" cy="16" r="1.2" fill="currentColor"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <line x1="12" y1="8"  x2="12" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
    <circle cx="12" cy="16" r="1.3" fill="white"/>`
};

// ─── ANALYTICS PIPELINE ──────────────────────────────────────────────────────

const SELECT_CONTEXT: TmaIconDef = {
  name: 'select-context',
  outline: `
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.3" stroke-dasharray="3 2"/>
    <line x1="12" y1="2"  x2="12" y2="5.5"  stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="12" y1="18.5" x2="12" y2="22" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="2"  y1="12" x2="5.5" y2="12"  stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="18.5" y1="12" x2="22" y2="12"  stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="12" r="4" fill="currentColor"/>
    <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2"/>
    <line x1="12" y1="2"  x2="12" y2="4.5"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="12" y1="19.5" x2="12" y2="22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="2"  y1="12" x2="4.5" y2="12"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="19.5" y1="12" x2="22" y2="12"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
};

const DATA_INGESTION: TmaIconDef = {
  name: 'data-ingestion',
  outline: `
    <polyline points="16,16 12,12 8,16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="12" y1="12" x2="12" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M20.4 18.6A9 9 0 1014.1 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <path d="M20.4 18.6A9 9 0 1014.1 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <polyline points="16,16 12,12 8,16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="12" y1="12" x2="12" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
};

const COPPER_INGESTION: TmaIconDef = {
  name: 'copper-ingestion',
  outline: `
    <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <rect x="7.5" y="7.5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <rect x="4" y="4" width="16" height="16" rx="3" fill="currentColor"/>
    <rect x="7.5" y="7.5" width="9" height="9" rx="1.5" fill="white"/>`
};

const BRONZE_INGESTION: TmaIconDef = {
  name: 'bronze-ingestion',
  outline: `
    <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M14 16h2m0 0h2m-2 0v-2m0 2v2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" fill="currentColor"/>
    <path d="M14 16h2m0 0h2m-2 0v-2m0 2v2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`
};

const FACILITY_CLASSIFICATION: TmaIconDef = {
  name: 'facility-classification',
  outline: `
    <path d="M3 21h18M5 21V9l7-6 7 6v12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <polyline points="9,13 12,11 15,13" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12" y1="11" x2="12" y2="21" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <path d="M3 21h18M5 21V9l7-6 7 6v12" fill="currentColor" stroke="currentColor" stroke-width="0.8"/>
    <polyline points="9,13 12,11 15,13" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="12" y1="11" x2="12" y2="21" stroke="white" stroke-width="1.2"/>`
};

const PAYER_CLASSIFICATION: TmaIconDef = {
  name: 'payer-classification',
  outline: `
    <rect x="2" y="7" width="20" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M2 11h20" stroke="currentColor" stroke-width="1.3"/>
    <polyline points="8,16 10,18 14,14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <rect x="2" y="7" width="20" height="14" rx="2" fill="currentColor"/>
    <path d="M2 11h20" stroke="white" stroke-width="1.3"/>
    <polyline points="8,16 10,18 14,14" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`
};

const DENIAL_CLASSIFICATION: TmaIconDef = {
  name: 'denial-classification',
  outline: `
    <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="14,4 14,9 19,9" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="9"  y1="13" x2="14" y2="17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="14" y1="13" x2="9"  y2="17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" fill="currentColor"/>
    <polyline points="14,4 14,9 19,9" fill="white" stroke="white" stroke-width="1.1"/>
    <line x1="9"  y1="13" x2="14" y2="17" stroke="white" stroke-width="1.7" stroke-linecap="round"/>
    <line x1="14" y1="13" x2="9"  y2="17" stroke="white" stroke-width="1.7" stroke-linecap="round"/>`
};

const DIAGNOSIS_CLASSIFICATION: TmaIconDef = {
  name: 'diagnosis-classification',
  outline: `
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <rect x="9" y="3" width="6" height="4" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="8" y1="12" x2="13" y2="12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <polyline points="13,14 15,16 13,18" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" fill="currentColor"/>
    <rect x="9" y="3" width="6" height="4" rx="1" fill="currentColor" stroke="white" stroke-width="1"/>
    <line x1="8" y1="12" x2="13" y2="12" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <polyline points="13,14 15,16 13,18" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`
};

const PHYSICIAN_CLASSIFICATION: TmaIconDef = {
  name: 'physician-classification',
  outline: `
    <circle cx="12" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <polyline points="8,4 10,6 14,2" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="12" cy="7" r="4" fill="currentColor"/>
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <polyline points="8,4 10,6 14,2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`
};

const ADJUSTMENT_CLASSIFICATION: TmaIconDef = {
  name: 'adjustment-classification',
  outline: `
    <path d="M12 2l1.4 4.2h4.4L14.2 9l1.4 4.2-3.6-2.6-3.6 2.6 1.4-4.2-3.6-2.8h4.4z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
    <line x1="6"  y1="18" x2="18" y2="18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="9"  y1="21" x2="15" y2="21" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <path d="M12 2l1.4 4.2h4.4L14.2 9l1.4 4.2-3.6-2.6-3.6 2.6 1.4-4.2-3.6-2.8h4.4z" fill="currentColor"/>
    <line x1="6"  y1="18" x2="18" y2="18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
    <line x1="9"  y1="21" x2="15" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
};

const FEE_SCHEDULE_BUILD: TmaIconDef = {
  name: 'fee-schedule-build',
  outline: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="3" y1="9"  x2="21" y2="9"  stroke="currentColor" stroke-width="1.2"/>
    <line x1="9" y1="9"  x2="9"  y2="21" stroke="currentColor" stroke-width="1.2"/>
    <path d="M6 13h1.5M6 16h1.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <path d="M13 13h2M13 16h3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor"/>
    <line x1="3" y1="9"  x2="21" y2="9"  stroke="white" stroke-width="1.3"/>
    <line x1="9" y1="9"  x2="9"  y2="21" stroke="white" stroke-width="1.3"/>
    <path d="M6 13h1.5M6 16h1.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M13 13h2M13 16h3" stroke="white" stroke-width="1.3" stroke-linecap="round"/>`
};

const MEDICARE_RATES: TmaIconDef = {
  name: 'medicare-rates',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M12 7v1.5M12 15.5V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M9.5 14.5c0 1.1.9 2 2.5 2s2.5-.9 2.5-2-1.1-1.7-2.5-2-2.5-.9-2.5-2 .9-2 2.5-2 2.5.9 2.5 2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <path d="M12 7v1.5M12 15.5V17" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M9.5 14.5c0 1.1.9 2 2.5 2s2.5-.9 2.5-2-1.1-1.7-2.5-2-2.5-.9-2.5-2 .9-2 2.5-2 2.5.9 2.5 2" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round"/>`
};

const SILVER_ANALYTICS: TmaIconDef = {
  name: 'silver-analytics',
  outline: `
    <path d="M3 20l5-9 4 6 3-5 5 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <path d="M3 20l5-9 4 6 3-5 5 8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>`
};

const GOLD_AGGREGATION: TmaIconDef = {
  name: 'gold-aggregation',
  outline: `
    <polygon points="12,2 15.5,8.5 22,9.5 17,14.5 18.5,21 12,17.5 5.5,21 7,14.5 2,9.5 8.5,8.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>`,
  solid: `
    <polygon points="12,2 15.5,8.5 22,9.5 17,14.5 18.5,21 12,17.5 5.5,21 7,14.5 2,9.5 8.5,8.5" fill="currentColor"/>`
};

/**
 * Pipeline — connected-stage flow mark.
 * Three nodes wired in series by a flow line, representing a multi-stage data
 * pipeline (e.g. SFTP → Databricks → Gold). Used by the Pipeline Console nav
 * item and page header.
 */
const PIPELINE: TmaIconDef = {
  name: 'pipeline',
  outline: `
    <line x1="6.6" y1="6" x2="10.4" y2="6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="13.6" y1="6" x2="17.4" y2="6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M19 8v3a3 3 0 01-3 3H8a3 3 0 00-3 3v0" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="5"  cy="6"  r="2.2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="12" cy="6"  r="2.2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="19" cy="6"  r="2.2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <circle cx="5"  cy="18" r="2.2" fill="none" stroke="currentColor" stroke-width="1.5"/>`,
  solid: `
    <line x1="6.6" y1="6" x2="10.4" y2="6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="13.6" y1="6" x2="17.4" y2="6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M19 8v3a3 3 0 01-3 3H8a3 3 0 00-3 3v0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="5"  cy="6"  r="2.6" fill="currentColor"/>
    <circle cx="12" cy="6"  r="2.6" fill="currentColor"/>
    <circle cx="19" cy="6"  r="2.6" fill="currentColor"/>
    <circle cx="5"  cy="18" r="2.6" fill="currentColor"/>`
};

/**
 * Report Subscription — envelope with a clock badge.
 * A scheduled / automatic email report (who gets what, when). Used by the
 * Report Subscriptions admin nav item and page header.
 */
const REPORT_SUBSCRIPTION: TmaIconDef = {
  name: 'report-subscription',
  outline: `
    <rect x="2.5" y="5" width="14" height="10.5" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="3,6 9.5,11 16,6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="17" cy="16.5" r="4.6" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="17,14.2 17,16.7 18.7,17.8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <path d="M4.1 5h11.8a1.6 1.6 0 011.6 1.6v4.3a5.6 5.6 0 00-6.1 4.6H4.1a1.6 1.6 0 01-1.6-1.6V6.6A1.6 1.6 0 014.1 5z" fill="currentColor"/>
    <polyline points="3.4,6.4 9.5,11 15.6,6.4" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="17" cy="16.5" r="4.8" fill="currentColor"/>
    <polyline points="17,14.2 17,16.7 18.7,17.8" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
};

// ─── INGESTION TOOLS ─────────────────────────────────────────────────────────

const MAP_TO_TMA: TmaIconDef = {
  name: 'map-to-tma',
  outline: `
    <path d="M21 12a9 9 0 01-9 9M3 12a9 9 0 019-9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <polyline points="3,8 3,12 7,12"   fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <polyline points="21,16 21,12 17,12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <path d="M21 12a9 9 0 01-9 9M3 12a9 9 0 019-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <polyline points="3,8 3,12 7,12"    fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
    <polyline points="21,16 21,12 17,12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`
};

const ENCOUNTER_ANALYTICS: TmaIconDef = {
  name: 'encounter-analytics',
  outline: `
    <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="8"  y1="11" x2="14" y2="11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="11" y1="8"  x2="11" y2="14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <path d="M21 21l-4.35-4.35A7 7 0 1116.65 16.65L21 21z" fill="currentColor"/>
    <line x1="8"  y1="11" x2="14" y2="11" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="11" y1="8"  x2="11" y2="14" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const ENCOUNTER_FLOW: TmaIconDef = {
  name: 'encounter-flow',
  outline: `
    <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="6"  cy="12" r="2.5" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <circle cx="18" cy="12" r="2.5" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <line x1="6"  y1="7" x2="6"  y2="9.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12" y1="5" x2="12" y2="9.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="18" y1="7" x2="18" y2="9.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="6"  cy="12" r="3" fill="currentColor"/>
    <circle cx="12" cy="12" r="3" fill="currentColor"/>
    <circle cx="18" cy="12" r="3" fill="currentColor"/>
    <line x1="6"  y1="7" x2="6"  y2="9"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="12" y1="5" x2="12" y2="9"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="18" y1="7" x2="18" y2="9"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
};

const SESSION_CPT_ANALYSIS: TmaIconDef = {
  name: 'session-cpt-analysis',
  outline: `
    <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" stroke-width="1.2"/>
    <line x1="7" y1="13" x2="9" y2="13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="7" y1="16" x2="9" y2="16" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <path d="M12 12v4l3-2-3-2z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>`,
  solid: `
    <rect x="3" y="4" width="18" height="16" rx="2" fill="currentColor"/>
    <line x1="3" y1="9" x2="21" y2="9" stroke="white" stroke-width="1.3"/>
    <line x1="7" y1="13" x2="9" y2="13" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="7" y1="16" x2="9" y2="16" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M12 12v4l3-2-3-2z" fill="white"/>`
};

const IMPORT_VALIDATION: TmaIconDef = {
  name: 'import-validation',
  outline: `
    <path d="M12 2L4 6v6c0 5.5 3.4 10.7 8 12 4.6-1.3 8-6.5 8-12V6l-8-4z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="9,12 11,14 15,10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <path d="M12 2L4 6v6c0 5.5 3.4 10.7 8 12 4.6-1.3 8-6.5 8-12V6l-8-4z" fill="currentColor"/>
    <polyline points="9,12 11,14 15,10" fill="none" stroke="white" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`
};

const DATA_CLEANUP: TmaIconDef = {
  name: 'data-cleanup',
  outline: `
    <polyline points="3,6 5,6 21,6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M10 6V4h4v2" fill="none" stroke="currentColor" stroke-width="1.4"/>`,
  solid: `
    <polyline points="3,6 5,6 21,6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" fill="currentColor"/>
    <path d="M10 6V4h4v2" fill="none" stroke="currentColor" stroke-width="1.6"/>`
};

const AUDIT_LOGS: TmaIconDef = {
  name: 'audit-logs',
  outline: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="11" cy="14" r="2"   fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="12.4" y1="15.4" x2="14" y2="17" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="currentColor"/>
    <polyline points="14,2 14,8 20,8" fill="white" stroke="white" stroke-width="1.1"/>
    <circle cx="11" cy="14" r="2.2" fill="white"/>
    <line x1="12.5" y1="15.5" x2="14" y2="17" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const ORCHESTRATION_MONITOR: TmaIconDef = {
  name: 'orchestration-monitor',
  outline: `
    <rect x="2"  y="2"  width="6" height="6" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <rect x="16" y="2"  width="6" height="6" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <rect x="9"  y="16" width="6" height="6" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <path d="M5 8v4h14V8M12 12v4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <rect x="2"  y="2"  width="6" height="6" rx="1.5" fill="currentColor"/>
    <rect x="16" y="2"  width="6" height="6" rx="1.5" fill="currentColor"/>
    <rect x="9"  y="16" width="6" height="6" rx="1.5" fill="currentColor"/>
    <path d="M5 8v4h14V8M12 12v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
};

const INGESTION_TEST: TmaIconDef = {
  name: 'ingestion-test',
  outline: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <path d="M10 12h4M10 15h2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="16" cy="15" r="2" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="currentColor"/>
    <polyline points="14,2 14,8 20,8" fill="white" stroke="white" stroke-width="1.1"/>
    <path d="M10 12h4M10 15h2" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <circle cx="16" cy="15" r="2.2" fill="white"/>`
};

const MEDICARE_INGESTION: TmaIconDef = {
  name: 'medicare-ingestion',
  outline: `
    <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <polyline points="8,16 12,12 8,8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12 12h4M16 8v8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor"/>
    <polyline points="8,16 12,12 8,8" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12 12h4M16 8v8" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const CMS_DATA_LOADER: TmaIconDef = {
  name: 'cms-data-loader',
  outline: `
    <ellipse cx="12" cy="5"  rx="9" ry="3" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M3 5v7c0 1.7 4 3 9 3s9-1.3 9-3V5"  fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M3 12v5c0 1.7 4 3 9 3s9-1.3 9-3v-5" fill="none" stroke="currentColor" stroke-width="1.5"/>`,
  solid: `
    <ellipse cx="12" cy="5" rx="9" ry="3" fill="currentColor"/>
    <path d="M21 5v7c0 1.7-4 3-9 3S3 13.7 3 12V5"  fill="currentColor"/>
    <path d="M21 12v5c0 1.7-4 3-9 3S3 18.7 3 17v-5" fill="currentColor"/>`
};

// ─── DATA MODEL ENTITIES ─────────────────────────────────────────────────────

const DM_PATIENT: TmaIconDef = {
  name: 'dm-patient',
  outline: `
    <circle cx="12" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M8 20.5 Q10 18.5 12 20 Q14 21.5 16 20.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="7" r="4" fill="currentColor"/>
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <path d="M8 20.5 Q10 18.5 12 20 Q14 21.5 16 20.5" fill="none" stroke="white" stroke-width="1.2" stroke-linecap="round"/>`
};

const DM_ENCOUNTER: TmaIconDef = {
  name: 'dm-encounter',
  outline: `
    <rect x="4" y="5" width="16" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="8"  y1="3" x2="8"  y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="4"  y1="10" x2="20" y2="10" stroke="currentColor" stroke-width="1.3"/>
    <circle cx="8.5"  cy="14.5" r="1.2" fill="currentColor"/>
    <circle cx="12"   cy="14.5" r="1.2" fill="currentColor"/>
    <circle cx="15.5" cy="14.5" r="1.2" fill="currentColor"/>`,
  solid: `
    <rect x="4" y="5" width="16" height="16" rx="2" fill="currentColor"/>
    <line x1="8"  y1="3" x2="8"  y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="4"  y1="10" x2="20" y2="10" stroke="white" stroke-width="1.3"/>
    <circle cx="8.5"  cy="14.5" r="1.4" fill="white"/>
    <circle cx="12"   cy="14.5" r="1.4" fill="white"/>
    <circle cx="15.5" cy="14.5" r="1.4" fill="white"/>`
};

const DM_SERVICE_LINE: TmaIconDef = {
  name: 'dm-service-line',
  outline: `
    <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="14,4 14,9 19,9" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <line x1="7" y1="13" x2="17" y2="13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="7" y1="16" x2="13" y2="16" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" fill="currentColor"/>
    <polyline points="14,4 14,9 19,9" fill="white" stroke="white" stroke-width="1.1"/>
    <line x1="7" y1="13" x2="17" y2="13" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="7" y1="16" x2="13" y2="16" stroke="white" stroke-width="1.4" stroke-linecap="round"/>`
};

const DM_CPT_CODE: TmaIconDef = {
  name: 'dm-cpt-code',
  outline: `
    <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M9 10.5a3 3 0 010 3M15 10.5a3 3 0 010 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-dasharray="1.5 1.5"/>`,
  solid: `
    <rect x="3" y="4" width="18" height="16" rx="2" fill="currentColor"/>
    <path d="M9 10.5a3 3 0 010 3M15 10.5a3 3 0 010 3" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="12" y1="8" x2="12" y2="16" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-dasharray="1.5 1.5"/>`
};

const DM_ICD10: TmaIconDef = {
  name: 'dm-icd10',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="12" y1="8"  x2="12" y2="13" stroke="currentColor" stroke-width="2"   stroke-linecap="round"/>
    <circle cx="12" cy="16" r="1.2" fill="currentColor"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <line x1="12" y1="8"  x2="12" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
    <circle cx="12" cy="16" r="1.3" fill="white"/>`
};

const DM_RENDERING_PHYSICIAN: TmaIconDef = {
  name: 'dm-rendering-physician',
  outline: `
    <circle cx="11" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M3 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="19.5" cy="17.5" r="3" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="19.5" y1="15.8" x2="19.5" y2="19.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="17.8" y1="17.5" x2="21.2" y2="17.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <circle cx="11" cy="7" r="4" fill="currentColor"/>
    <path d="M3 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <circle cx="19.5" cy="17.5" r="3.2" fill="currentColor"/>
    <line x1="19.5" y1="15.5" x2="19.5" y2="19.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="17.5" y1="17.5" x2="21.5" y2="17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const DM_PAYER: TmaIconDef = {
  name: 'dm-payer',
  outline: `
    <rect x="2" y="7" width="20" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M2 11h20" stroke="currentColor" stroke-width="1.3"/>
    <rect x="5" y="14" width="5" height="3" rx="1" fill="none" stroke="currentColor" stroke-width="1.2"/>`,
  solid: `
    <rect x="2" y="7" width="20" height="14" rx="2" fill="currentColor"/>
    <path d="M2 11h20" stroke="white" stroke-width="1.3"/>
    <rect x="5" y="14" width="5" height="3" rx="1" fill="white"/>`
};

const DM_FACILITY: TmaIconDef = {
  name: 'dm-facility',
  outline: `
    <path d="M3 21h18M5 21V7l7-4 7 4v14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <rect x="9"  y="14" width="3" height="4" fill="none" stroke="currentColor" stroke-width="1.2"/>
    <rect x="13" y="10" width="3" height="3" fill="none" stroke="currentColor" stroke-width="1.2"/>
    <rect x="8"  y="10" width="3" height="3" fill="none" stroke="currentColor" stroke-width="1.2"/>`,
  solid: `
    <path d="M3 21h18M5 21V7l7-4 7 4v14" fill="currentColor" stroke="currentColor" stroke-width="0.8"/>
    <rect x="9"  y="14" width="3" height="4" fill="white"/>
    <rect x="13" y="10" width="3" height="3" fill="white"/>
    <rect x="8"  y="10" width="3" height="3" fill="white"/>`
};

const DM_PAYMENT: TmaIconDef = {
  name: 'dm-payment',
  outline: `
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M12 7v1.5M12 15.5V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M9.5 14.5c0 1.1.9 2 2.5 2s2.5-.9 2.5-2-1.1-1.7-2.5-2-2.5-.9-2.5-2 .9-2 2.5-2 2.5.9 2.5 2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <path d="M12 7v1.5M12 15.5V17" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M9.5 14.5c0 1.1.9 2 2.5 2s2.5-.9 2.5-2-1.1-1.7-2.5-2-2.5-.9-2.5-2 .9-2 2.5-2 2.5.9 2.5 2" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round"/>`
};

const DM_ADJUSTMENT: TmaIconDef = {
  name: 'dm-adjustment',
  outline: `
    <path d="M12 3L3 9v12h6v-6h6v6h6V9L12 3z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <line x1="7"  y1="15" x2="10" y2="15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="14" y1="9"  x2="17" y2="9"  stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="7"  y1="9"  x2="17" y2="15" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-dasharray="2 1.5"/>`,
  solid: `
    <path d="M12 3L3 9v12h6v-6h6v6h6V9L12 3z" fill="currentColor"/>
    <line x1="7"  y1="15" x2="10" y2="15" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="14" y1="9"  x2="17" y2="9"  stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="7"  y1="9"  x2="17" y2="15" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-dasharray="2 1.5"/>`
};

const DM_DENIAL: TmaIconDef = {
  name: 'dm-denial',
  outline: `
    <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <polyline points="14,4 14,9 19,9" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="9"  y1="12" x2="15" y2="17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="15" y1="12" x2="9"  y2="17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`,
  solid: `
    <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" fill="currentColor"/>
    <polyline points="14,4 14,9 19,9" fill="white" stroke="white" stroke-width="1.1"/>
    <line x1="9"  y1="12" x2="15" y2="17" stroke="white" stroke-width="1.8" stroke-linecap="round"/>
    <line x1="15" y1="12" x2="9"  y2="17" stroke="white" stroke-width="1.8" stroke-linecap="round"/>`
};

const DM_FEE_SCHEDULE: TmaIconDef = {
  name: 'dm-fee-schedule',
  outline: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="7"  y1="8"  x2="17" y2="8"  stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="7"  y1="11" x2="17" y2="11" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="7"  y1="14" x2="13" y2="14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <path d="M15 13.5v4M13 15.5h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <rect x="3" y="3" width="18" height="18" rx="2" fill="currentColor"/>
    <line x1="7"  y1="8"  x2="17" y2="8"  stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="7"  y1="11" x2="17" y2="11" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="7"  y1="14" x2="13" y2="14" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M15 13.5v4M13 15.5h4" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const DM_REFERRING_PHYSICIAN: TmaIconDef = {
  name: 'dm-referring-physician',
  outline: `
    <circle cx="8" cy="7" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M2 21c0-4.2 3.1-7 6-7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="14" y1="11" x2="21" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M18 8.5 L21 11 L18 13.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <circle cx="8" cy="7" r="4" fill="currentColor"/>
    <path d="M2 21c0-4.2 3.1-7 6-7" fill="currentColor"/>
    <line x1="14" y1="11" x2="21" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <path d="M18 8.5 L21 11 L18 13.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
};

const DM_SUPERVISING_PHYSICIAN: TmaIconDef = {
  name: 'dm-supervising-physician',
  outline: `
    <circle cx="12" cy="9" r="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M8.5 4.5 L12 2 L15.5 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="12" y1="2" x2="12" y2="5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <circle cx="12" cy="9" r="4" fill="currentColor"/>
    <path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor"/>
    <path d="M8.5 4.5 L12 2 L15.5 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="12" y1="2" x2="12" y2="5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>`
};

const DM_CHARGEMASTER: TmaIconDef = {
  name: 'dm-chargemaster',
  outline: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <line x1="9"  y1="13" x2="11" y2="13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="9"  y1="16" x2="11" y2="16" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="13" y1="13" x2="15" y2="13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="13" y1="16" x2="15" y2="16" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8L14 2z" fill="currentColor"/>
    <polyline points="14,2 14,8 20,8" fill="white" stroke="white" stroke-width="1.2"/>
    <line x1="9"  y1="13" x2="11" y2="13" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="9"  y1="16" x2="11" y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="13" y1="13" x2="15" y2="13" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="13" y1="16" x2="15" y2="16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const DM_MODIFIER: TmaIconDef = {
  name: 'dm-modifier',
  outline: `
    <rect x="3" y="6" width="13" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="6"  y1="10" x2="13" y2="10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="6"  y1="13" x2="10" y2="13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="18.5" cy="8" r="3"   fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="18.5" y1="6.5" x2="18.5" y2="9.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="17"   y1="8"   x2="20"   y2="8"   stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>`,
  solid: `
    <rect x="3" y="6" width="13" height="12" rx="2" fill="currentColor"/>
    <line x1="6"  y1="10" x2="13" y2="10" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="6"  y1="13" x2="10" y2="13" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="18.5" cy="8" r="3.5" fill="currentColor"/>
    <line x1="18.5" y1="6.5" x2="18.5" y2="9.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="17"   y1="8"   x2="20"   y2="8"   stroke="white" stroke-width="1.5" stroke-linecap="round"/>`
};

const DM_PLACE_OF_SERVICE: TmaIconDef = {
  name: 'dm-place-of-service',
  outline: `
    <path d="M12 2C8.7 2 6 4.7 6 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6z" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <circle cx="12" cy="8" r="2.5" fill="none" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <path d="M12 2C8.7 2 6 4.7 6 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6z" fill="currentColor"/>
    <circle cx="12" cy="8" r="2.5" fill="white"/>`
};

// ─── DATE & TIME ─────────────────────────────────────────────────────────────

/**
 * date-period — Calendar with a horizontal range pill spanning multiple cells,
 * anchored by two endpoint dots. Reads as "a span of dates."
 * Used in widget footers to show the active date range.
 */
const DATE_PERIOD: TmaIconDef = {
  name: 'date-period',
  outline: `
    <rect x="4" y="5" width="16" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="8"  y1="3" x2="8"  y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="4"  y1="10" x2="20" y2="10" stroke="currentColor" stroke-width="1.3"/>
    <rect x="6" y="13.5" width="12" height="3" rx="1.5" fill="currentColor" opacity="0.85"/>
    <circle cx="6"  cy="15" r="1.6" fill="currentColor"/>
    <circle cx="18" cy="15" r="1.6" fill="currentColor"/>`,
  solid: `
    <rect x="4" y="5" width="16" height="16" rx="2" fill="currentColor"/>
    <line x1="8"  y1="3" x2="8"  y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="4"  y1="10" x2="20" y2="10" stroke="white" stroke-width="1.3"/>
    <rect x="6" y="13.5" width="12" height="3" rx="1.5" fill="white"/>
    <circle cx="6"  cy="15" r="1.6" fill="white"/>
    <circle cx="18" cy="15" r="1.6" fill="white"/>`
};

/**
 * date-field — Calendar with a single highlighted column and a chevron above it.
 * Reads as "the date column being filtered on."
 * Used in widget footers to show which date field drives the active filter.
 */
const DATE_FIELD: TmaIconDef = {
  name: 'date-field',
  outline: `
    <rect x="4" y="5" width="16" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="8"  y1="3" x2="8"  y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="4"  y1="10" x2="20" y2="10" stroke="currentColor" stroke-width="1.3"/>
    <rect x="10.5" y="11.5" width="3" height="8" rx="0.7" fill="currentColor" opacity="0.85"/>
    <path d="M12 8.3 L13.1 9.6 L10.9 9.6 Z" fill="currentColor"/>`,
  solid: `
    <rect x="4" y="5" width="16" height="16" rx="2" fill="currentColor"/>
    <line x1="8"  y1="3" x2="8"  y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="4"  y1="10" x2="20" y2="10" stroke="white" stroke-width="1.3"/>
    <rect x="10.5" y="11.5" width="3" height="8" rx="0.7" fill="white"/>
    <path d="M12 8.3 L13.1 9.6 L10.9 9.6 Z" fill="white"/>`
};

// ─── UI PRIMITIVES ───────────────────────────────────────────────────────────

const ARROW_FORWARD: TmaIconDef = {
  name: 'arrow-forward',
  outline: `
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`
};

const CLOSE: TmaIconDef = {
  name: 'close',
  outline: `
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`,
  solid: `
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>`
};

// ─── TEMPLATES & WORKSPACES ──────────────────────────────────────────────────
// Visual language (TEMPLATES_PLATFORM_PLAN.md §6): standard tools lead with the
// tool shape (pivot grid / explorer table); the accounting family leads with an
// open-ledger base and carries the tool as a small corner mark. Color comes from
// the surrounding Tailwind classes (currentColor), never from these SVGs.

// Template — reusable starting point: sheet with a mini layout + corner sparkle
const TEMPLATE: TmaIconDef = {
  name: 'template',
  outline: `
    <rect x="3.5" y="4.5" width="13.5" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="6.5" y1="8.5" x2="14" y2="8.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    <rect x="6.5" y="11.5" width="3.2" height="5.5" rx="0.8" fill="none" stroke="currentColor" stroke-width="1.3"/>
    <line x1="12" y1="12.5" x2="14" y2="12.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12" y1="15.5" x2="14" y2="15.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M19.5 2.5l.75 1.75L22 5l-1.75.75-.75 1.75-.75-1.75L17 5l1.75-.75z" fill="currentColor"/>`,
  solid: `
    <rect x="3.5" y="4.5" width="13.5" height="16" rx="2" fill="currentColor"/>
    <line x1="6.5" y1="8.5" x2="14" y2="8.5" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
    <rect x="6.5" y="11.5" width="3.2" height="5.5" rx="0.8" fill="white"/>
    <line x1="12" y1="12.5" x2="14" y2="12.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12" y1="15.5" x2="14" y2="15.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M19.5 2.5l.75 1.75L22 5l-1.75.75-.75 1.75-.75-1.75L17 5l1.75-.75z" fill="currentColor"/>`
};

// Pivot — grid with emphasized header row + header column
const TMA_PIVOT: TmaIconDef = {
  name: 'pivot',
  outline: `
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="3.5" y1="9" x2="20.5" y2="9" stroke="currentColor" stroke-width="1.6"/>
    <line x1="9" y1="3.5" x2="9" y2="20.5" stroke="currentColor" stroke-width="1.6"/>
    <line x1="9" y1="14.75" x2="20.5" y2="14.75" stroke="currentColor" stroke-width="1.2"/>
    <line x1="14.75" y1="9" x2="14.75" y2="20.5" stroke="currentColor" stroke-width="1.2"/>`,
  solid: `
    <path d="M5.5 3.5h15a2 2 0 012 2V9h-17z" transform="translate(-2 0)" fill="currentColor"/>
    <path d="M3.5 9H9v11.5H5.5a2 2 0 01-2-2z" fill="currentColor"/>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="9" y1="14.75" x2="20.5" y2="14.75" stroke="currentColor" stroke-width="1.2"/>
    <line x1="14.75" y1="9" x2="14.75" y2="20.5" stroke="currentColor" stroke-width="1.2"/>`
};

// Explorer — data table + magnifier lens
const TMA_EXPLORER: TmaIconDef = {
  name: 'explorer',
  outline: `
    <rect x="3" y="4.5" width="14" height="15" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="3" y1="9" x2="17" y2="9" stroke="currentColor" stroke-width="1.4"/>
    <line x1="6" y1="12.5" x2="13" y2="12.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="6" y1="15.5" x2="11.5" y2="15.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="17.2" cy="16.2" r="3.3" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <line x1="19.6" y1="18.6" x2="21.8" y2="20.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`,
  solid: `
    <path d="M5 4.5h10a2 2 0 012 2v5.4a4.8 4.8 0 00-4.9 7.6H5a2 2 0 01-2-2v-11a2 2 0 012-2z" fill="currentColor"/>
    <line x1="3.4" y1="9" x2="16.6" y2="9" stroke="white" stroke-width="1.4"/>
    <line x1="6" y1="12.5" x2="11.5" y2="12.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="17.2" cy="16.2" r="3.3" fill="none" stroke="currentColor" stroke-width="2"/>
    <line x1="19.6" y1="18.6" x2="21.8" y2="20.8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
};

// Accounting — open ledger: center spine, posting lines left, Σ right
const TMA_ACCOUNTING: TmaIconDef = {
  name: 'accounting',
  outline: `
    <path d="M3 6c2-1.2 5-1.2 9 .8 4-2 7-2 9-.8v12.5c-2-1.2-5-1.2-9 .8-4-2-7-2-9-.8z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    <line x1="12" y1="6.8" x2="12" y2="19.3" stroke="currentColor" stroke-width="1.4"/>
    <line x1="5.5" y1="9.5" x2="9.5" y2="9.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="5.5" y1="12.5" x2="9.5" y2="12.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M14.8 9.5h3.6l-2.3 2.5 2.3 2.5h-3.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
  solid: `
    <path d="M3 6c2-1.2 5-1.2 9 .8 4-2 7-2 9-.8v12.5c-2-1.2-5-1.2-9 .8-4-2-7-2-9-.8z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
    <line x1="12" y1="6.8" x2="12" y2="19.3" stroke="white" stroke-width="1.4"/>
    <line x1="5.5" y1="9.5" x2="9.5" y2="9.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="5.5" y1="12.5" x2="9.5" y2="12.5" stroke="white" stroke-width="1.3" stroke-linecap="round"/>
    <path d="M14.8 9.5h3.6l-2.3 2.5 2.3 2.5h-3.6" fill="none" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`
};

// Accounting Pivot — ledger base + pivot-grid corner mark
const TMA_PIVOT_ACCOUNTING: TmaIconDef = {
  name: 'pivot-accounting',
  outline: `
    <path d="M3 5.2c1.8-1.1 4.5-1.1 8 .7 3.5-1.8 6.2-1.8 8-.7v8.3h-4.5v4.3c-1.2.2-2.4.6-3.5 1.2-3.5-1.8-6.2-1.8-8-.7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    <line x1="11" y1="5.9" x2="11" y2="17.8" stroke="currentColor" stroke-width="1.3"/>
    <line x1="5.2" y1="8.6" x2="8.8" y2="8.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="5.2" y1="11.4" x2="8.8" y2="11.4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <rect x="14.5" y="13.5" width="7" height="7" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="14.5" y1="16.2" x2="21.5" y2="16.2" stroke="currentColor" stroke-width="1.3"/>
    <line x1="17.2" y1="13.5" x2="17.2" y2="20.5" stroke="currentColor" stroke-width="1.3"/>`,
  solid: `
    <path d="M3 5.2c1.8-1.1 4.5-1.1 8 .7 3.5-1.8 6.2-1.8 8-.7v8.3h-4.5v4.3c-1.2.2-2.4.6-3.5 1.2-3.5-1.8-6.2-1.8-8-.7z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
    <line x1="11" y1="5.9" x2="11" y2="17.8" stroke="white" stroke-width="1.3"/>
    <line x1="5.2" y1="8.6" x2="8.8" y2="8.6" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
    <rect x="14.5" y="13.5" width="7" height="7" rx="1.2" fill="currentColor"/>
    <line x1="15.2" y1="16.2" x2="20.8" y2="16.2" stroke="white" stroke-width="1.2"/>
    <line x1="17.2" y1="14.2" x2="17.2" y2="19.8" stroke="white" stroke-width="1.2"/>`
};

// Accounting Explorer — ledger base + magnifier corner mark
const TMA_EXPLORER_ACCOUNTING: TmaIconDef = {
  name: 'explorer-accounting',
  outline: `
    <path d="M3 5.2c1.8-1.1 4.5-1.1 8 .7 3.5-1.8 6.2-1.8 8-.7v7.1a4.9 4.9 0 00-6.6 5.5c-.5.2-.9.4-1.4.7-3.5-1.8-6.2-1.8-8-.7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    <line x1="11" y1="5.9" x2="11" y2="17.6" stroke="currentColor" stroke-width="1.3"/>
    <line x1="5.2" y1="8.6" x2="8.8" y2="8.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="5.2" y1="11.4" x2="8.8" y2="11.4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="17" cy="16.8" r="3.1" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="19.3" y1="19.1" x2="21.4" y2="21.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <path d="M3 5.2c1.8-1.1 4.5-1.1 8 .7 3.5-1.8 6.2-1.8 8-.7v7.1a4.9 4.9 0 00-6.6 5.5c-.5.2-.9.4-1.4.7-3.5-1.8-6.2-1.8-8-.7z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
    <line x1="11" y1="5.9" x2="11" y2="17.6" stroke="white" stroke-width="1.3"/>
    <line x1="5.2" y1="8.6" x2="8.8" y2="8.6" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="17" cy="16.8" r="3.1" fill="none" stroke="currentColor" stroke-width="1.9"/>
    <line x1="19.3" y1="19.1" x2="21.4" y2="21.2" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>`
};

// Report — sheet with rising chart line (reserved for Kind=report)
const TMA_REPORT: TmaIconDef = {
  name: 'report',
  outline: `
    <rect x="4.5" y="3.5" width="15" height="17" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <polyline points="7.5,14 10.5,10.5 13,12.5 16.5,7.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="7.5" y1="17" x2="16.5" y2="17" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>`,
  solid: `
    <rect x="4.5" y="3.5" width="15" height="17" rx="2" fill="currentColor"/>
    <polyline points="7.5,14 10.5,10.5 13,12.5 16.5,7.5" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="7.5" y1="17" x2="16.5" y2="17" stroke="white" stroke-width="1.3" stroke-linecap="round"/>`
};

// Folder — template organization tree
const TMA_FOLDER: TmaIconDef = {
  name: 'folder',
  outline: `
    <path d="M3 7a2 2 0 012-2h4.6l2 2.5H19a2 2 0 012 2V17a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>`,
  solid: `
    <path d="M3 7a2 2 0 012-2h4.6l2 2.5H19a2 2 0 012 2V17a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="currentColor"/>`
};

// Favorite — 5-point star (outline = off, solid = on)
const TMA_FAVORITE: TmaIconDef = {
  name: 'favorite',
  outline: `
    <path d="M12 3.5l2.5 5.4 5.9.7-4.4 4 1.2 5.9L12 16.5l-5.2 3-1.2-5.9-4.4-4 5.9-.7z" transform="translate(1.1 0)" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>`,
  solid: `
    <path d="M12 3.5l2.5 5.4 5.9.7-4.4 4 1.2 5.9L12 16.5l-5.2 3-1.2-5.9-4.4-4 5.9-.7z" transform="translate(1.1 0)" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>`
};

// User Group — two overlapping people (sharing audiences)
const TMA_USER_GROUP: TmaIconDef = {
  name: 'user-group',
  outline: `
    <circle cx="9.5" cy="8.5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.6"/>
    <path d="M3.5 19.5c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M15.4 5.9a2.9 2.9 0 110 5.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M17 14.4c2.2.6 3.5 2.4 3.5 4.8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`,
  solid: `
    <circle cx="9.5" cy="8.5" r="3.2" fill="currentColor"/>
    <path d="M3.5 19.5c0-3.3 2.7-5.4 6-5.4s6 2.1 6 5.4z" fill="currentColor"/>
    <path d="M15.4 5.9a2.9 2.9 0 110 5.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
    <path d="M17 14.4c2.2.6 3.5 2.4 3.5 4.8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`
};

// ─── MASTER EXPORT ────────────────────────────────────────────────────────────

export const TMA_ICONS: TmaIconDef[] = [
  // UI Primitives
  ARROW_FORWARD,
  CLOSE,
  // Date & Time
  DATE_PERIOD,
  DATE_FIELD,
  // AI & Brand
  TMINSIGHTS,
  TMAI,
  // People & Roles
  PATIENT,
  PHYSICIAN,
  PHYSICIANS,
  REFERRING_PHYSICIAN,
  SUPERVISING_PHYSICIAN,
  USERS,
  // Analytics Navigation
  PRACTICE_DASHBOARD,
  TRIAD_DASHBOARD,
  TMA_DASHBOARD,
  USER_DASHBOARD,
  PRACTICE_OVERVIEW,
  PRACTICE_DETAIL,
  REVENUE_ENHANCEMENT,
  CHARGEMASTER_REVIEW,
  REIMBURSEMENT_VARIANCE,
  SEQUENCING_RECOVERY,
  OPERATIONS_METRICS,
  CLINIC_PERFORMANCE,
  WORK_RVU,
  CHARGE_PROCESSING,
  NEW_PATIENT_ANALYTICS,
  QUALITY_COMPLIANCE,
  MEDICARE_COMPLIANCE,
  QUALITY_INCENTIVES,
  PATIENT_OUTCOMES,
  DATA_EXTRACTION,
  ONBOARDING,
  ANALYTICS_DASHBOARD,
  TRIAD_OVERVIEW,
  // Admin Navigation
  ADMIN_DASHBOARD,
  ORGANIZATIONS,
  USER_MANAGEMENT,
  SILVER_EXTRACTION,
  SILVER_EXTRACTION_V2,
  REPORT_WIZARD,
  GANDALF,
  TRY_QUERY,
  LOCATION_METRICS,
  REFERRING_DOCTORS,
  PATIENT_RETENTION,
  DIAGNOSIS_ANALYSIS,
  // Analytics Pipeline
  SELECT_CONTEXT,
  DATA_INGESTION,
  COPPER_INGESTION,
  BRONZE_INGESTION,
  FACILITY_CLASSIFICATION,
  PAYER_CLASSIFICATION,
  DENIAL_CLASSIFICATION,
  DIAGNOSIS_CLASSIFICATION,
  PHYSICIAN_CLASSIFICATION,
  ADJUSTMENT_CLASSIFICATION,
  FEE_SCHEDULE_BUILD,
  MEDICARE_RATES,
  SILVER_ANALYTICS,
  GOLD_AGGREGATION,
  PIPELINE,
  REPORT_SUBSCRIPTION,
  // Ingestion Tools
  MAP_TO_TMA,
  ENCOUNTER_ANALYTICS,
  ENCOUNTER_FLOW,
  SESSION_CPT_ANALYSIS,
  IMPORT_VALIDATION,
  DATA_CLEANUP,
  AUDIT_LOGS,
  ORCHESTRATION_MONITOR,
  INGESTION_TEST,
  MEDICARE_INGESTION,
  CMS_DATA_LOADER,
  // Templates & Workspaces
  TEMPLATE,
  TMA_PIVOT,
  TMA_EXPLORER,
  TMA_ACCOUNTING,
  TMA_PIVOT_ACCOUNTING,
  TMA_EXPLORER_ACCOUNTING,
  TMA_REPORT,
  TMA_FOLDER,
  TMA_FAVORITE,
  TMA_USER_GROUP,
  // Data Model Entities
  DM_PATIENT,
  DM_ENCOUNTER,
  DM_SERVICE_LINE,
  DM_CPT_CODE,
  DM_ICD10,
  DM_RENDERING_PHYSICIAN,
  DM_PAYER,
  DM_FACILITY,
  DM_PAYMENT,
  DM_ADJUSTMENT,
  DM_DENIAL,
  DM_FEE_SCHEDULE,
  DM_REFERRING_PHYSICIAN,
  DM_SUPERVISING_PHYSICIAN,
  DM_CHARGEMASTER,
  DM_MODIFIER,
  DM_PLACE_OF_SERVICE,
];

export const TMA_ICON_NAMES = TMA_ICONS.map(i => i.name) as readonly string[];
export type TmaIconName = typeof TMA_ICON_NAMES[number];
